import moment from "moment";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { ApiResponse } from "../../../api/apiResponse";
import { createInstantSession } from "../../../api/endpoints/tutors/sessions";
import SessionModel from "../../../api/models/SessionModel";
import StudentModel from "../../../api/models/StudentModel";
import { isValidMoment } from "../../../helpers/date_helpers";
import { useInfiniteTutorStudents } from "../../../hooks/useInfiniteTutorStudents";
import AppButton from "../../buttons/AppButton";
import DropDown from "../../forms/dropdown/DropDown";
import Modal from "../ModalWithFooter";
import { useNavigate } from "react-router-dom";
import { openSessionInNewTab } from "../../sessions/helper";

type InstantSessionModalProps = {
  isOpen: boolean;
  onClose: () => void;
  getStudentLastSchedule: (key: number) => any;
};
export default function InstantSessionModal(props: InstantSessionModalProps) {
  const { isOpen, onClose, getStudentLastSchedule } = props;
  const dateNow = new Date().toUTCString();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const { t } = useTranslation("", {
    keyPrefix: "page.tutor.dashboard.instantSession",
  });
  const { students, loadMoreStudents } = useInfiniteTutorStudents(true);
  const studentsOptions = students.map((student) => ({
    key: student.name,
    value: `${student.id}`,
    image: student.profile_picture_url,
  }));
  const [selectedStudent, setSelectedStudent] =
    useState<StudentModel | undefined>();

  // get prev session info based on studentId
  const getSessionData = (studentId: number) => {
    const studentData = students.findIndex(
      (student) => student.id === studentId
    );
    setSelectedStudent(students[studentData]);
  };
  const onScheduleSession = async (data: any) => {
    setIsLoading(true);
    const response: ApiResponse = await createInstantSession(data);
    if (response.isSuccess) {
      toast.success(t("successSession"));
      onClose();
      openSessionInNewTab("tutor")(response?.data?.id);
      setIsLoading(false);
    } else {
      setIsLoading(false);
      response.errorMessages.forEach((errorMessage) => {
        toast.error(errorMessage);
      });
    }
  };
  useEffect(() => {
    if (isOpen) {
      setSelectedStudent(undefined);
    }
  }, [isOpen]);

  return (
    <Modal open={isOpen} setOpen={onClose}>
      <Modal.Body setOpen={onClose} title={t("createInstantSession")}>
        <div>
          <DropDown
            name="tutor-students"
            onBottomReached={loadMoreStudents}
            schema={{}}
            register={() => {}}
            setValue={(studentId: string) => getSessionData(+studentId)}
            defaultValue={`${selectedStudent?.id}`}
            label={t("selectStudent")}
            placeholder={t("studentName")}
            error={false}
            options={studentsOptions}
            className="w-full"
            asSelector
            hasImage
          />
        </div>
        {!!selectedStudent && (
          <PrevSessionInfo
            getStudentLastSchedule={getStudentLastSchedule}
            student={selectedStudent}
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className="flex gap-4 px-2">
          <AppButton buttonShape="secondary" onClick={() => onClose()}>
            {t("cancel")}
          </AppButton>
          <AppButton
            buttonShape="primary"
            onClick={() => {
              onScheduleSession({
                student_id: selectedStudent?.id,
                date: moment(),
                start_time: moment(),
              });
            }}
            disabled={!selectedStudent?.id || isLoading}
          >
            {t("createSession")}
          </AppButton>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

const PrevSessionInfo = (props: {
  student: StudentModel;
  getStudentLastSchedule: (key: number) => any;
}) => {
  const { student, getStudentLastSchedule } = props;
  const [lastSession, setLastSession] = useState<SessionModel | null>(null);
  const { t } = useTranslation("", {
    keyPrefix: "page.tutor.dashboard.instantSession",
  });
  const fetchLastSession = async () => {
    let lastSessionData = await getStudentLastSchedule(student?.id);
    const session = new SessionModel(lastSessionData?.[0]);
    setLastSession(session);
  };

  useEffect(() => {
    fetchLastSession();
  }, [student]);

  if (!lastSession?.id)
    return (
      <p className="mt-8 text-sm font-normal text-gray-400 text-center">
        {t("noPrevSession")}
      </p>
    );

  return (
    <>
      <h5 className="mt-8 mb-4 text-sm font-medium text-gray-700">
        {t("prevSessionInfo")}
      </h5>
      <div className="flex flex-col gap-8">
        <InfoCard
          title={t("sessionNumber")}
          value={`${t("session")} #${lastSession.sessionNumber || ""}`}
        />
        {isValidMoment(lastSession.date) && (
          <InfoCard
            title={t("sessionDate")}
            value={lastSession.date?.format("YYYY/M/D dddd")!}
          />
        )}
        <InfoCard
          title={t("readingProgress")}
          value={`${t("page")} ${lastSession.lastQuranPageReached || 0}`}
        />
      </div>
    </>
  );
};

const InfoCard = ({ title, value }: { title: string; value: string }) => {
  return (
    <div className="flex flex-col gap-0.5 pb-2 border-b border-gray-200">
      <h6 className="text-xs leading-5 font-medium text-gray-500">{title}</h6>
      <p className="text-base font-medium text-gray-700">{value}</p>
    </div>
  );
};
